import React from "react";
import { useLiabilitySummary } from "../hooks/useTreasury";

const usd = (value) =>
  Number(value || 0).toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const CARDS = {
  merchant: {
    label: "Total merchant wallet liability (USD)",
    hint: "Every merchant wallet balance plus the house merchant's wallet, converted to USD. This is what partners need to cover.",
    // Falls back to merchants only if the API predates merchantTotalUsd.
    pick: (d) => d.merchantTotalUsd ?? d.merchantUsd,
    // Shown under the total so the house share is visible.
    detail: (d) =>
      d.merchantTotalUsd == null ? null : `Merchants ${usd(d.merchantUsd)} + house merchant ${usd(d.houseMerchantUsd)}`,
  },
  house: {
    label: "House merchant wallet liability (USD)",
    hint: "The house merchant's own wallet balance, the collection wallet consumer deposits pass through.",
    pick: (d) => d.houseMerchantUsd,
  },
  consumer: {
    label: "Consumer wallet liability (USD)",
    hint: "What consumers hold across all their wallets, including reserved balance, converted to USD.",
    pick: (d) => d.consumerUsd,
  },
};

// Liability totals in USD for the selected day. Uses the USD value saved with
// that day's snapshot; a day computed before that existed is priced at today's
// rate and tagged as an estimate. A currency with no rate is left out and named.
function LiabilityCards({ date, show }) {
  const { data, isLoading, isError } = useLiabilitySummary(date);

  if (isError) {
    return (
      <p className="px-4 py-3 mb-4 text-sm text-red-700 bg-red-50 rounded border border-red-100">
        Could not load the liability totals.
      </p>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex flex-wrap gap-4">
        {show.map((key) => {
          const card = CARDS[key];
          return (
            <div key={key} className="flex-1 px-4 py-3 rounded border border-gray-200 bg-gray-50/50 min-w-[240px]">
              <div className="text-xs font-bold tracking-wider text-gray-500 uppercase">{card.label}</div>
              <div className="text-xl font-semibold text-gray-900">{isLoading || !data ? "..." : usd(card.pick(data))}</div>
              {data && card.detail?.(data) && <p className="mt-1 text-xs font-medium text-gray-600">{card.detail(data)}</p>}
              <p className="mt-1 text-xs text-gray-400">{card.hint}</p>
            </div>
          );
        })}
      </div>
      {data?.estimated && (
        <p
          className="mt-2 text-xs text-amber-700"
          title={`Priced at today's rate: ${data.estimatedCurrencies.join(", ")}`}
        >
          Estimate: this day was saved before USD values were recorded, so {data.estimatedCurrencies.join(", ")} are priced at today's rate.
        </p>
      )}
      {data?.unpricedCurrencies?.length > 0 && (
        <p className="mt-2 text-xs text-red-600">
          Not included, no USD rate available: {data.unpricedCurrencies.join(", ")}.
        </p>
      )}
    </div>
  );
}

export default LiabilityCards;
